"use client";
import { createContext, Dispatch, ReactNode, SetStateAction, useState, useEffect, useContext, useCallback, useRef } from "react";
import { Project, File, isFile, isDirectory, Directory, createFile } from '@lib/fileSystem';
import { IDEContext } from "@contexts*";
import { db, autoSaveFile, getProgenesisProject, loadDbProject, addFileToDb, getLastFileIdFromDb, saveIDEState, saveProjectToDb, createDbProject, deleteFileFromDb } from "@lib/db";

interface ProjectContextValue {
  project: Project | null;
  setProject: Dispatch<SetStateAction<Project | null>>;
  openFiles: File[];
  refreshOpenFiles: (loadedProject: Project) => void;
  simFile: File | undefined, 
  onFileChangeCallback: (file: File) => void
  handleCreateProject: (projectName: string) => void
  loadProject:() => Promise<void>
  addNewFileToProject: (name: string) => Promise<void>
  updateOpenFiles: (fileIds: number[]) => void
  removeFileFromProject: (fileIds: number[]) => Promise<{ success: boolean; removedFileIds: number[] }>
}

export const ProjectContext = createContext<ProjectContextValue>({} as ProjectContextValue);

interface ProjectContextProviderProps {
  children: ReactNode;
  initialProject: Project | null;
}


export const findOpenFilesRecursive = (node: Directory, openFileIds: number[]): File[] => {
  let openFiles: File[] = [];
  if(!node) return openFiles

  for (const child of node.children) {
    if (isFile(child) && openFileIds.includes(child.id)) {
      const fileId = openFileIds.indexOf(child.id);

      // TODO: Trying to keep control of order
      openFiles[fileId] = child;
    } else if (isDirectory(child)) {
      openFiles = [...openFiles, ...findOpenFilesRecursive(child, openFileIds)];
    }
  }
  
  return openFiles;
};


export const ProjectContextProvider: React.FC<ProjectContextProviderProps> = ({
  children,
  initialProject,
}) => {
  const { iDEState, setLoading, setIDEState, setTabChanged, setRefresh } = useContext(IDEContext);
  const [project, setProject] = useState<Project | null>(initialProject);
  const [openFiles, setOpenFiles] = useState<File[]>([]);
  const [simFile, setSimFile] = useState<File | undefined>();
  const timeoutRef = useRef<NodeJS.Timeout>();
  
  const updateOpenFiles = (fileIds: number[], proj: Project | null = project) => {
    if(!proj || !project) return
    const open = findOpenFilesRecursive((proj || (project as Project)).root, fileIds);
    setOpenFiles(open);
    // console.log({ fileIds });
  }

  const refreshOpenFiles = async () => {
    if (!iDEState?.openFileIds) return;
    console.log('refreshOpenFiles', iDEState?.openFileIds, iDEState.focusedFileIdx);
    const loadedProject = await loadDbProject();
    updateOpenFiles(iDEState?.openFileIds);
    const sFile = loadedProject.root.children.find((child: { id: any; }) => child.id === loadedProject.simFileId)
    setSimFile(sFile as File);
    return loadedProject
  };

  const completeRefreshOpenFiles = async () => {
    console.log('completeRefreshOpenFiles');
    const loadedProject = await loadDbProject();
    setIDEState({
      ...iDEState,
      openFileIds: [0],
      focusedFileIdx: 0,
    });
    updateOpenFiles([0]);
    console.log({ loadedProject });
    const sFile = loadedProject.root.children.find((child: { id: any; }) => child.id === loadedProject.simFileId);
    // console.log({ sFile });
    setSimFile(sFile as File);
    return loadedProject;
  };

  useEffect(() => {
    // console.log('Project state changed', { project, openFiles, ids: iDEState?.openFileIds });
    const refreshProject = async () => {
      try {
        const dbProj = await refreshOpenFiles()
        if (JSON.stringify(project) !== JSON.stringify(dbProj)) {
          setProject(dbProj)
        }
      } catch (error) {
        console.error('refreshProject failed:', error);
      }
    };
    const completeRefreshProject = async (project: Project | null) => {
      try {
        const saveProj = await saveProjectToDb(project);
        completeRefreshOpenFiles();
      } catch (error) {
        console.error('refreshProject failed:', error);
      }
    }
    //if (!project) {
     // refreshProject();
    //}
    // console.log('What is going on? ', { project, openFiles, iDEState })
    if (openFiles.length !== iDEState?.openFileIds?.length && !project?.root) {
      refreshProject()
    } else if (openFiles.length !== iDEState?.openFileIds?.length) {
      const newOpenFileList = iDEState?.openFileIds
      updateOpenFiles(newOpenFileList)
    } else {
      completeRefreshProject(project)
    }
  }, [project]);


  const debouncedAutoSave = useCallback(async (file: File) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(async () => {
      try {
        const dbUpdateRes = await autoSaveFile(file);

        console.log('Autosaved', file.name, dbUpdateRes)
        console.log({openFiles: iDEState?.openFileIds})
        updateOpenFiles(iDEState?.openFileIds)
        if (!dbUpdateRes) {
          throw new Error("Could not update dbState");
        }
      } catch (error) {
        console.error("Autosave failed:", error);
      }
    }, 333);
  }, []);

  const debouncedProjectLoad = useCallback(async (project: Project) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(async () => {
      try {
        await refreshOpenFiles()
        setProject(project)
      } catch (error) {
        console.error("Autosave failed:", error);
      }
    }, 33);
  }, []);

  const onFileChangeCallback = useCallback(
    async (file: File) => {
      const { data } = file;
      if (data === "") return;
      // const projectClone = { ...project } as Project;
      
      try {
        debouncedAutoSave(file);
        // const { proj, error } = replaceProjectFileByPath(projectClone, file);
        // if (error) {
        //   throw error;
        // } else {
        //   setProject(proj);
        //   if (proj) {
        //   }
        // }
      } catch (error) {
        console.error("File update failed:", error);
      }
    },
    [project, setProject]//, debouncedAutoSave]
  );

  const handleCreateProject = useCallback(
    async (projectName: string ) => {
      try {
        setLoading(true)
        try {
          const newProj = await createDbProject(projectName)
          console.log({ newProj })
          setProject(newProj) 
          const openFileIds = [0]; 
          const focusedFileIdx = 0; 
          const newState = {
            id: 1, 
            openFileIds,
            focusedFileIdx,
          }
          // const stateUpdateRes = await saveIDEState(newState)
          // console.log({stateUpdateRes})
          /* if(stateUpdateRes) {
            setIDEState(newState);
            updateOpenFiles(openFileIds)
            // setIdeStateUpdated(true);
          } */
          const stateUpdateRes = await saveIDEState(newState)

          console.log({ newState, stateUpdateRes })
          setIDEState(newState);
          updateOpenFiles(openFileIds);
          // debouncedProjectLoad(newProj)
        } catch (e) {
          throw new Error(`Could not add project: ${e}`);
        }
      } catch (error) {
        console.error("Error creating new project:", error);
      } finally {
        setLoading(false)
      }
    },
    [setProject]
  );

  const loadProject = async () => {
      try {
        console.log('loadProject')
        setLoading(true)
        const loadProjRes = await loadDbProject()
        if(loadProjRes.error) throw loadProjRes.error
        // console.log({loadProjRes})
        debouncedProjectLoad(loadProjRes)
      } catch (error) {
        console.error("Error loading project:", error);
      }finally{
        setLoading(false)
      }
    }
    // Add this state to track when project update is complete
    const [newFileAdded, setFileAdded] = useState(false);
    // Add this state to track when IDE state update is complete
    const [ideStateUpdated, setIdeStateUpdated] = useState(false);

 // First useEffect now queries the DB for last ID
  useEffect(() => {
    if (newFileAdded) {
      const getLastId = async () => {
        try {
          const lastId = await getLastFileIdFromDb(); // Add this function to query your DB
          const openFileIds = [...iDEState.openFileIds, lastId];
          const focusedFileIdx = openFileIds.length -1
          console.log({ lastId, openFileIds, focusedFileIdx })
          const newState = {
          ...iDEState, 
          openFileIds,
          focusedFileIdx,
        }
            console.log('Saving state', newState, lastId)
        const stateUpdateRes = await saveIDEState(newState)
        console.log({stateUpdateRes})
          if(stateUpdateRes){
            setIDEState(newState);
            updateOpenFiles(openFileIds)
            // setIdeStateUpdated(true);
          }
          // const dbSaveRes = await saveProjectToDb(project)
          // if(!dbSaveRes) throw new Error(dbSaveRes)
          setFileAdded(false); // Reset for next update
          // refreshOpenFiles(project)
        } catch (error) {
          console.error("Error adding file to IDE project:", error);
        }
      };

      getLastId();
    }
  }, [newFileAdded]);

  const addNewFileToProject = async (name: string) => {
      try {
        setLoading(true)
        console.log('addNewFileToProject')
        const periodSplit = name.split('.')
        const ext = periodSplit.pop()
        const data = ext === 'sx' ? `// ${name}\n` : ''
        const newFile = createFile(name, `/${name}`, data, ext)
        const addFileRes = await addFileToDb(newFile)
        if(addFileRes.error) throw addFileRes.error
        newFile.id = addFileRes
        if(addFileRes > 0){
           project?.root.children.push(newFile);

          const dbSaveRes = await saveProjectToDb(project)
          if(!dbSaveRes) throw new Error(dbSaveRes)
          setProject(project)
          setFileAdded(true); // Trigger the first useEffect
        }
        // setRefresh(true)     
      } catch (error) {
        console.error("Error loading project:", error);
      }finally{
        setLoading(false)
      }
    }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

const removeFileFromProject = async (fileIds: number[]) => {
  try {
    setLoading(true);
    console.log('removeFileFromProject', fileIds);
    
    if (!project) {
      throw new Error('No project loaded');
    }

    // Remove files from project structure
    const originalChildren = [...project.root.children];
    project.root.children = project.root.children.filter(
      (child) => !fileIds.includes(child.id)
    );

    // Remove files from database
    for (const fileId of fileIds) {
      try {
        const deleteResult = await deleteFileFromDb(fileId)
        if (!deleteResult) {
          console.warn(`Failed to delete file with ID ${fileId} from database`);
        }
      } catch (error) {
        console.error(`Error deleting file ${fileId} from database:`, error);
        // Continue with other deletions even if one fails
      }
    }

    // Update open files - remove deleted files from open tabs
    const updatedOpenFileIds = iDEState.openFileIds.filter(
      (fileId: number) => !fileIds.includes(fileId)
    );
    
    // Adjust focused file index if necessary
    let newFocusedFileIdx = iDEState.focusedFileIdx;
    
    if (updatedOpenFileIds.length === 0) {
      // If no files are open, default to first available file or file with id 0
      const remainingFiles = project.root.children;
      if (remainingFiles.length > 0) {
        updatedOpenFileIds.push(remainingFiles[0].id);
        newFocusedFileIdx = 0;
      } else {
        // Fallback to id 0 if no files remain
        updatedOpenFileIds.push(0);
        newFocusedFileIdx = 0;
      }
    } else if (iDEState.focusedFileIdx >= updatedOpenFileIds.length) {
      // If focused file was deleted and was the last tab, focus previous tab
      newFocusedFileIdx = updatedOpenFileIds.length - 1;
    }

    // Update IDE state
    const newState = {
      ...iDEState,
      openFileIds: updatedOpenFileIds,
      focusedFileIdx: newFocusedFileIdx,
    };

    const stateUpdateRes = await saveIDEState(newState);
    if (!stateUpdateRes) {
      console.warn('Could not update IDE state, but continuing...');
    }

    // Save updated project to database
    const dbSaveRes = await saveProjectToDb(project);
    if (!dbSaveRes) {
      console.warn('Could not save project to database, but continuing...');
    }

    // Update React state
    setProject({ ...project });
    setIDEState(newState);
    updateOpenFiles(updatedOpenFileIds);

    console.log(`Successfully removed ${fileIds.length} file(s) from project`);
    return { success: true, removedFileIds: fileIds };

  } catch (error) {
    console.error("Error removing files from project:", error);
    
    // Attempt to rollback project state if possible
    if (project) {
      try {
        await loadProject(); // Reload project to restore state
      } catch (rollbackError) {
        console.error("Failed to rollback project state:", rollbackError);
      }
    }
    
    throw error;
  } finally {
    setLoading(false);
  }
};
  return (
    <ProjectContext.Provider value={{ project, setProject, openFiles, refreshOpenFiles, simFile, onFileChangeCallback, handleCreateProject, loadProject, addNewFileToProject, updateOpenFiles, removeFileFromProject  }}>
      {children}
    </ProjectContext.Provider>
  );
};

export default ProjectContextProvider;